import { FC, ReactNode, RefObject, useState } from 'react';

import { Button } from '@purpur/library';
import { IconChevronDown } from '@purpur/library/icon/chevron-down';
import { IconChevronUp } from '@purpur/library/icon/chevron-up';
import AnimateHeight from 'react-animate-height';

import { MIN_HEIGHT_TO_SHOW_EXPAND } from './constants';
import { ButtonWrapper, Container, StyledWrapper } from './ShowMoreOrLess.styles';

export type ButtonComponentType = FC<{ children: ReactNode; onClick: () => void; 'aria-expanded': boolean }>;

type Props = {
    children: ReactNode;
    scrollBackToRef?: RefObject<HTMLElement | null>;
    initialView?: 'expanded' | 'collapsed';
    onExpanded?: () => void;
    minHeightToShowExpand?: number;
    dimmerOffsetHeight?: number;
    showMoreText?: string;
    showLessText?: string;
    ButtonComponent?: ButtonComponentType;
    'data-test'?: string;
    className?: string;
};
const DefaultButton: ButtonComponentType = ({ children, ...props }) => (
    <ButtonWrapper>
        <Button {...props} variant="primary" type="button">
            {children}
        </Button>
    </ButtonWrapper>
);

export const ShowMoreOrLess: FC<Props> = ({
    children,
    initialView = 'collapsed',
    scrollBackToRef,
    onExpanded = () => undefined,
    minHeightToShowExpand = MIN_HEIGHT_TO_SHOW_EXPAND,
    dimmerOffsetHeight = MIN_HEIGHT_TO_SHOW_EXPAND,
    showMoreText = 'Rodyti daugiau',
    showLessText = 'Rodyti mažiau',
    ButtonComponent = DefaultButton,
    'data-test': dataTest,
    className,
}) => {
    const [childHeight, setChildHeight] = useState<number>(0);
    const child = <div ref={(el) => setChildHeight(el?.clientHeight || 0)}>{children}</div>;

    const [isShowMore, setIsShowMore] = useState({ collapsed: true, expanded: false }[initialView]);

    if (childHeight < minHeightToShowExpand) {
        return <Container className={className}>{child}</Container>;
    }

    const onShowMoreClick = () => {
        if (isShowMore) {
            setIsShowMore(!isShowMore);
            onExpanded();
        } else {
            scrollBackToRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            window.setTimeout(() => {
                setIsShowMore(!isShowMore);
            }, 150);
        }
    };

    return (
        <Container className={className} data-test={dataTest}>
            <StyledWrapper $isShowMore={isShowMore}>
                <AnimateHeight height={isShowMore ? dimmerOffsetHeight : 'auto'} duration={500}>
                    {child}
                </AnimateHeight>
            </StyledWrapper>
            <ButtonComponent onClick={onShowMoreClick} aria-expanded={!isShowMore}>
                {isShowMore ? showMoreText : showLessText}
                {isShowMore ? <IconChevronDown size="sm" /> : <IconChevronUp size="sm" />}
            </ButtonComponent>
        </Container>
    );
};
